import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  FlatList,
  Image,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  Pressable,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';
import { Button, Text } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import JobsContext from './context/JobsContext';
import { Palette, palettes, radius, shadow, spacing } from './theme';
import { track } from './lib/analytics';

const isWeb = Platform.OS === 'web';
const ONBOARDING_MAX_WIDTH = 560;

type SlideKey =
  | 'welcome'
  | 'search'
  | 'favorites'
  | 'tracker'
  | 'alerts'
  | 'resume'
  | 'account'
  | 'settings';

interface SlideDef {
  key: SlideKey;
  renderMockup: (palette: Palette, t: (k: string) => string) => React.ReactNode;
}

// The onboarding/tour is intentionally pinned to the dark palette to preserve the
// branded look regardless of the user's chosen theme — see CLAUDE.md theming exception.
export default function Onboarding() {
  const router = useRouter();
  const { t } = useTranslation();
  const { completeOnboarding } = useContext(JobsContext);
  const { replay } = useLocalSearchParams<{ replay?: string }>();
  const isReplay = replay === '1';

  const { width } = useWindowDimensions();
  const slideWidth = Math.min(width, ONBOARDING_MAX_WIDTH);

  const palette = palettes.dark;
  const listRef = useRef<FlatList<SlideDef>>(null);
  const [index, setIndex] = useState(0);

  const slides = useMemo<SlideDef[]>(
    () => [
      { key: 'welcome', renderMockup: renderWelcomeMockup },
      { key: 'search', renderMockup: renderSearchMockup },
      { key: 'favorites', renderMockup: renderFavoritesMockup },
      { key: 'tracker', renderMockup: renderTrackerMockup },
      { key: 'alerts', renderMockup: renderAlertsMockup },
      { key: 'resume', renderMockup: renderResumeMockup },
      { key: 'account', renderMockup: renderAccountMockup },
      { key: 'settings', renderMockup: renderSettingsMockup },
    ],
    []
  );

  useEffect(() => {
    track('tour_view_step', { step: index });
  }, [index]);

  const finish = useCallback(() => {
    if (isReplay) {
      if (router.canGoBack()) router.back();
      else router.replace('/(tabs)');
      return;
    }
    completeOnboarding();
    track('onboarding_complete');
    router.replace('/(tabs)');
  }, [isReplay, router, completeOnboarding]);

  const handleNext = useCallback(() => {
    if (index < slides.length - 1) {
      const nextIndex = index + 1;
      setIndex(nextIndex);
      listRef.current?.scrollToIndex({ index: nextIndex, animated: true });
      return;
    }
    finish();
  }, [index, slides.length, finish]);

  const handleSkip = useCallback(() => {
    track('tour_skip', { step: index });
    finish();
  }, [index, finish]);

  const onMomentumEnd = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const newIndex = Math.round(e.nativeEvent.contentOffset.x / slideWidth);
      setIndex((prev) => (prev === newIndex ? prev : newIndex));
    },
    [slideWidth]
  );

  const isLast = index === slides.length - 1;
  const styles = useMemo(() => makeStyles(palette, slideWidth), [palette, slideWidth]);

  return (
    <SafeAreaView
      style={[styles.safeArea, { backgroundColor: palette.canvas }]}
      edges={['top', 'bottom']}
    >
      <FlatList
        ref={listRef}
        data={slides}
        keyExtractor={(item) => item.key}
        horizontal
        pagingEnabled
        snapToInterval={slideWidth}
        decelerationRate="fast"
        showsHorizontalScrollIndicator={false}
        bounces={false}
        onMomentumScrollEnd={onMomentumEnd}
        getItemLayout={(_, i) => ({ length: slideWidth, offset: slideWidth * i, index: i })}
        initialNumToRender={slides.length}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        renderItem={({ item }) => (
          <View style={styles.slide}>
            <Text style={styles.slideTitle}>{t(`tour.slides.${item.key}.title`)}</Text>
            <Text style={styles.slideDescription}>
              {t(`tour.slides.${item.key}.description`)}
            </Text>
            <View style={styles.mockupArea}>{item.renderMockup(palette, t)}</View>
          </View>
        )}
      />

      <View style={styles.footer}>
        <View style={styles.dots}>
          {slides.map((s, i) => (
            <View
              key={s.key}
              style={[
                styles.dot,
                i === index && styles.dotActive,
              ]}
            />
          ))}
        </View>

        <View style={styles.footerRow}>
          <Pressable
            onPress={handleSkip}
            hitSlop={12}
            accessibilityRole="button"
            disabled={isLast}
            style={({ pressed }) => [
              styles.skipBtn,
              { opacity: isLast ? 0 : pressed ? 0.6 : 1 },
            ]}
          >
            <Text style={styles.skipText}>{t('tour.skip')}</Text>
          </Pressable>

          <Button
            mode="contained"
            onPress={handleNext}
            buttonColor={palette.primary}
            textColor={palette.canvas}
            style={styles.cta}
            contentStyle={styles.ctaContent}
            labelStyle={styles.ctaLabel}
            icon={({ color, size }) => (
              <MaterialCommunityIcons
                name={isLast ? 'check-circle' : 'arrow-right-circle'}
                color={color}
                size={size}
              />
            )}
          >
            {isLast ? t('tour.getStarted') : t('tour.next')}
          </Button>
        </View>
      </View>
    </SafeAreaView>
  );
}

function renderWelcomeMockup(palette: Palette, t: (k: string) => string) {
  return (
    <View style={[heroStyles.heroSection, { backgroundColor: palette.surface }]}>
      {/* eslint-disable-next-line @typescript-eslint/no-require-imports */}
      <Image source={require('../assets/images/icon.png')} style={heroStyles.heroLogo} />
      <View style={[heroStyles.bubble, { backgroundColor: '#2A2E36' }]}>
        <Text style={[heroStyles.bubbleText, { color: palette.textOnDark }]}>
          {t('onboarding.tagline')}
        </Text>
      </View>
    </View>
  );
}

function renderSearchMockup(palette: Palette, t: (k: string) => string) {
  const light = palettes.light;
  return (
    <View style={{ width: '100%' }}>
      <View style={[heroStyles.heroSection, { backgroundColor: palette.surface, height: 140 }]}>
        <MaterialCommunityIcons name="magnify" size={64} color={palette.primary} />
      </View>
      <View style={[heroStyles.searchPreview, { backgroundColor: light.surface }]}>
        <MaterialCommunityIcons name="magnify" size={18} color={light.textMuted} />
        <Text style={[heroStyles.searchPreviewText, { color: light.textSecondary }]}>
          {t('search.placeholder')}
        </Text>
      </View>
      <View style={heroStyles.filterRow}>
        <View style={[heroStyles.filterChip, { backgroundColor: light.surface }]}>
          <MaterialCommunityIcons name="filter-variant" size={18} color={light.textPrimary} />
          <Text style={[heroStyles.filterChipText, { color: light.textPrimary }]}>
            {t('filter.title')}
          </Text>
          <View style={[heroStyles.filterBadge, { backgroundColor: palette.primary }]}>
            <Text style={[heroStyles.filterBadgeText, { color: palette.canvas }]}>3</Text>
          </View>
        </View>
      </View>
    </View>
  );
}

function renderFavoritesMockup(palette: Palette, t: (k: string) => string) {
  const light = palettes.light;
  return (
    <View style={[heroStyles.previewCard, { backgroundColor: light.surface }]}>
      <View style={heroStyles.previewCardHeader}>
        <View style={{ flex: 1 }}>
          <Text style={[heroStyles.previewTitle, { color: light.textPrimary }]}>
            Software Developer
          </Text>
          <Text style={[heroStyles.previewCompany, { color: light.textSecondary }]}>
            Google
          </Text>
        </View>
        <MaterialCommunityIcons name="heart" size={22} color={palette.favorite} />
      </View>
      <View style={heroStyles.previewCardFooter}>
        <View style={[heroStyles.datePill, { backgroundColor: light.surfaceMuted }]}>
          <Text style={[heroStyles.previewDate, { color: light.textSecondary }]}>
            26 Nov 2025
          </Text>
        </View>
        <View style={[heroStyles.previewBadge, { backgroundColor: light.appliedBg }]}>
          <MaterialCommunityIcons
            name="check-circle"
            size={14}
            color={light.appliedText}
          />
          <Text style={[heroStyles.previewBadgeText, { color: light.appliedText }]}>
            {t('job.applied')}
          </Text>
        </View>
      </View>
    </View>
  );
}

function renderTrackerMockup(palette: Palette, t: (k: string) => string) {
  const light = palettes.light;
  const stages = [
    { label: t('applications.status.applied'), date: '28 Nov', done: true },
    { label: t('applications.status.interviewing'), date: '2 Dec', done: true },
    { label: t('applications.status.offer'), date: '', done: false },
  ];
  return (
    <View style={[heroStyles.previewCard, { backgroundColor: light.surface }]}>
      <Text style={[heroStyles.previewTitle, { color: light.textPrimary }]}>Frontend Engineer</Text>
      <Text style={[heroStyles.previewCompany, { color: light.textSecondary }]}>Acme Inc</Text>
      <View style={trackerStyles.timeline}>
        {stages.map((s) => (
          <View key={s.label} style={trackerStyles.stageRow}>
            <View
              style={[
                trackerStyles.stageDot,
                { borderColor: palette.primary },
                s.done && { backgroundColor: palette.primary },
              ]}
            />
            <Text style={[trackerStyles.stageLabel, { color: light.textPrimary }]}>{s.label}</Text>
            <Text style={[trackerStyles.stageDate, { color: light.textMuted }]}>{s.date}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

function renderAlertsMockup(palette: Palette, t: (k: string) => string) {
  const light = palettes.light;
  return (
    <View style={[heroStyles.previewCard, { backgroundColor: light.surface }]}>
      <View style={trackerStyles.alertHeader}>
        <MaterialCommunityIcons name="bell-ring-outline" size={22} color={palette.primary} />
        <Text style={[heroStyles.previewTitle, { color: light.textPrimary }]}>react · remote · senior</Text>
      </View>
      <View style={[trackerStyles.matchPill, { backgroundColor: light.appliedBg }]}>
        <MaterialCommunityIcons name="briefcase-check-outline" size={14} color={light.appliedText} />
        <Text style={[trackerStyles.matchPillText, { color: light.appliedText }]}>
          {t('alerts.matchingNow').replace('{{count}}', '12')}
        </Text>
      </View>
    </View>
  );
}

function renderAccountMockup(palette: Palette, t: (k: string) => string) {
  const light = palettes.light;
  const items = [
    { icon: 'file-document-outline' as const, label: t('tabs.resumeBuilder') },
    { icon: 'briefcase-outline' as const, label: t('tabs.applications') },
    { icon: 'heart-outline' as const, label: t('tabs.favorites') },
    { icon: 'bell-outline' as const, label: t('alerts.title') },
  ];
  return (
    <View style={[heroStyles.previewCard, { backgroundColor: light.surface }]}>
      <View style={trackerStyles.alertHeader}>
        <MaterialCommunityIcons name="cloud-check-outline" size={24} color={palette.primary} />
        <Text style={[heroStyles.previewTitle, { color: light.textPrimary }]}>{t('auth.account')}</Text>
      </View>
      {items.map((it) => (
        <View key={it.label} style={trackerStyles.syncRow}>
          <MaterialCommunityIcons name={it.icon} size={18} color={light.textSecondary} />
          <Text style={[trackerStyles.syncLabel, { color: light.textPrimary }]}>{it.label}</Text>
          <MaterialCommunityIcons name="check-circle" size={16} color={light.appliedText} />
        </View>
      ))}
    </View>
  );
}

function renderResumeMockup(palette: Palette, _t: (k: string) => string) {
  const light = palettes.light;
  return (
    <View style={[heroStyles.previewCard, { backgroundColor: light.surface }]}>
      <View style={heroStyles.resumeRow}>
        <MaterialCommunityIcons
          name="file-document-edit-outline"
          size={36}
          color={palette.primary}
        />
        <View style={{ flex: 1 }}>
          <Text style={[heroStyles.previewTitle, { color: light.textPrimary }]}>
            Alex Johnson
          </Text>
          <Text style={[heroStyles.previewCompany, { color: light.textSecondary }]}>
            Senior Frontend Engineer
          </Text>
        </View>
      </View>
      <View style={heroStyles.resumeLine} />
      <View style={[heroStyles.resumeLine, { width: '70%' }]} />
      <View style={[heroStyles.resumeLine, { width: '85%' }]} />
      <View style={heroStyles.resumeChipRow}>
        <View style={[heroStyles.resumeChip, { backgroundColor: light.surfaceMuted }]}>
          <Text style={[heroStyles.resumeChipText, { color: light.textPrimary }]}>PDF</Text>
        </View>
        <View style={[heroStyles.resumeChip, { backgroundColor: light.surfaceMuted }]}>
          <Text style={[heroStyles.resumeChipText, { color: light.textPrimary }]}>DOCX</Text>
        </View>
      </View>
    </View>
  );
}

function renderSettingsMockup(palette: Palette, t: (k: string) => string) {
  const light = palettes.light;
  return (
    <View style={[heroStyles.previewCard, { backgroundColor: light.surface }]}>
      <View style={heroStyles.settingsRow}>
        <View style={[heroStyles.themeChip, { backgroundColor: light.appliedBg }]}>
          <MaterialCommunityIcons name="weather-sunny" size={16} color={light.appliedText} />
          <Text style={[heroStyles.themeChipText, { color: light.appliedText }]}>
            {t('settings.themeLight')}
          </Text>
        </View>
        <View style={[heroStyles.themeChip, { backgroundColor: light.surfaceMuted }]}>
          <MaterialCommunityIcons name="weather-night" size={16} color={light.textPrimary} />
          <Text style={[heroStyles.themeChipText, { color: light.textPrimary }]}>
            {t('settings.themeDark')}
          </Text>
        </View>
      </View>
      <View style={[heroStyles.languageRow, { backgroundColor: light.surfaceMuted }]}>
        <MaterialCommunityIcons name="translate" size={18} color={light.textPrimary} />
        <Text style={[heroStyles.languageLabel, { color: light.textPrimary }]}>
          English / Português / 日本語
        </Text>
      </View>
    </View>
  );
}

const makeStyles = (p: Palette, slideWidth: number) =>
  StyleSheet.create({
    safeArea: { flex: 1 },
    list: {
      flex: 1,
      alignSelf: 'center',
      width: '100%',
      maxWidth: isWeb ? ONBOARDING_MAX_WIDTH : undefined,
    },
    listContent: { alignItems: 'stretch' },
    slide: {
      width: slideWidth,
      paddingHorizontal: spacing.lg,
      paddingTop: spacing.lg,
      paddingBottom: spacing.md,
    },
    slideTitle: {
      fontSize: 22,
      fontWeight: '700',
      color: p.textOnDark,
      textAlign: 'center',
    },
    slideDescription: {
      marginTop: spacing.xs,
      fontSize: 14,
      lineHeight: 20,
      color: p.textSecondary,
      textAlign: 'center',
      paddingHorizontal: spacing.sm,
    },
    mockupArea: {
      marginTop: spacing.lg,
      flex: 1,
      justifyContent: 'center',
    },
    footer: {
      width: '100%',
      maxWidth: isWeb ? ONBOARDING_MAX_WIDTH : undefined,
      alignSelf: 'center',
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.lg,
    },
    dots: {
      flexDirection: 'row',
      justifyContent: 'center',
      gap: spacing.xs,
      marginBottom: spacing.md,
    },
    dot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: p.border,
    },
    dotActive: {
      width: 22,
      backgroundColor: p.primary,
    },
    footerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    skipBtn: { paddingVertical: spacing.sm, paddingHorizontal: spacing.sm },
    skipText: { color: p.textSecondary, fontSize: 14, fontWeight: '600' },
    cta: { borderRadius: radius.pill },
    ctaContent: {
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.xs,
      flexDirection: 'row-reverse',
    },
    ctaLabel: { fontSize: 15, fontWeight: '700' },
  });

const heroStyles = StyleSheet.create({
  heroSection: {
    height: 220,
    borderRadius: radius.lg,
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  heroLogo: { width: 96, height: 96, opacity: 0.5 },
  bubble: {
    position: 'absolute',
    top: spacing.lg,
    right: spacing.lg,
    maxWidth: '60%',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderRadius: radius.md,
  },
  bubbleText: { fontSize: 13, lineHeight: 18 },
  previewCard: {
    borderRadius: radius.md,
    padding: spacing.md,
    ...shadow.card,
  },
  previewCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: spacing.sm,
  },
  previewTitle: { fontSize: 15, fontWeight: '700' },
  previewCompany: { fontSize: 13, marginTop: 2 },
  previewCardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  datePill: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: radius.sm,
  },
  previewDate: { fontSize: 12 },
  previewBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: radius.pill,
    gap: 4,
  },
  previewBadgeText: { fontSize: 11, fontWeight: '600' },
  searchPreview: {
    marginTop: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderRadius: radius.pill,
    gap: spacing.sm,
  },
  searchPreviewText: { fontSize: 14 },
  filterRow: {
    marginTop: spacing.sm,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.pill,
  },
  filterChipText: { fontSize: 13, fontWeight: '600' },
  filterBadge: {
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    paddingHorizontal: 5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  filterBadgeText: { fontSize: 11, fontWeight: '700' },
  resumeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  resumeLine: {
    height: 8,
    width: '100%',
    borderRadius: 4,
    backgroundColor: '#E2E5EB',
    marginBottom: spacing.xs,
  },
  resumeChipRow: {
    flexDirection: 'row',
    gap: spacing.xs,
    marginTop: spacing.sm,
  },
  resumeChip: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: radius.sm,
  },
  resumeChipText: { fontSize: 11, fontWeight: '700' },
  settingsRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  themeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.pill,
  },
  themeChipText: { fontSize: 13, fontWeight: '600' },
  languageRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderRadius: radius.md,
  },
  languageLabel: { fontSize: 13, fontWeight: '500' },
});

const trackerStyles = StyleSheet.create({
  timeline: { marginTop: spacing.md, gap: spacing.sm },
  stageRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  stageDot: { width: 12, height: 12, borderRadius: 6, borderWidth: 2 },
  stageLabel: { flex: 1, fontSize: 14, fontWeight: '600' },
  stageDate: { fontSize: 12 },
  alertHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.md },
  matchPill: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.pill,
  },
  matchPillText: { fontSize: 13, fontWeight: '700' },
  syncRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
  },
  syncLabel: { flex: 1, fontSize: 14, fontWeight: '500' },
});
